import type { FieldMissionItem } from '@groupe-reca/contracts'
import { distanceMeters, type GeoPoint } from '../application/tracking/geo'

// Ordre de présentation des résidences sur la tablette. Une suggestion, jamais une contrainte : le serveur accepte
// une résidence terminée dans n'importe quel ordre, et l'ordre de route (`order`) reste affiché sur chaque carte.

/** `NEAREST` : la plus proche d'abord (à vol d'oiseau); `ROUTE` : ordre de la route, choisi par l'opérateur. */
export type ResidenceSort = 'NEAREST' | 'ROUTE'

/** Point où se rend l'opérateur : le drapeau (entrée) s'il est connu, sinon la position de l'adresse. */
export function destinationOf(item: FieldMissionItem): GeoPoint | null {
  const { flag, latitude, longitude } = item.snapshot
  if (flag) return { lat: flag.latitude, lng: flag.longitude }
  return latitude !== null && longitude !== null ? { lat: latitude, lng: longitude } : null
}

export function byRouteOrder(items: readonly FieldMissionItem[]): FieldMissionItem[] {
  return [...items].sort((a, b) => a.order - b.order)
}

/**
 * Plus proche : distance à vol d'oiseau depuis `from`, à égalité l'ordre de route. Sans position connue, ou pour
 * une résidence sans coordonnées (placée après les autres), l'ordre de route s'applique.
 */
export function sortResidences(
  items: readonly FieldMissionItem[],
  sort: ResidenceSort,
  from: GeoPoint | null,
): FieldMissionItem[] {
  const ordered = byRouteOrder(items)
  if (sort === 'ROUTE' || !from) return ordered
  const distance = new Map(
    ordered.map((item) => {
      const destination = destinationOf(item)
      return [item.id, destination ? distanceMeters(from, destination) : Number.POSITIVE_INFINITY] as const
    }),
  )
  return ordered.sort((a, b) => (distance.get(a.id) ?? 0) - (distance.get(b.id) ?? 0) || a.order - b.order)
}

/** Cellule de 18 à 25 m : le tri ne bouge qu'après un vrai déplacement, pas à chaque relevé GPS. */
const anchorStepDeg = 0.000_225

export function sortAnchor(position: GeoPoint | null): GeoPoint | null {
  if (!position) return null
  const snap = (value: number) => Math.round(value / anchorStepDeg) * anchorStepDeg
  return { lat: snap(position.lat), lng: snap(position.lng) }
}
